/* -------------------------------------------------- */
/*  src/components/PartnerDetailAdvanced.js           */
/*  提供 EditPartnerDialog  &  ShareMenu 组件          */
/* -------------------------------------------------- */
import { QRCodeCanvas } from "qrcode.react";
import React, { useState, useRef } from "react";
import { doc, updateDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db, storage } from "../firebase"; // storage: Firebase Storage 实例
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { Dialog } from "@headlessui/react";
import { toast } from "react-hot-toast";
import QRCode from "qrcode.react";
import {
  Pencil,
  Save,
  X,
  Share2,
  Clipboard,
  Twitter,
  Linkedin,
  Code,
  QrCode,
} from "lucide-react";

/* ---------- 工具函数 ---------- */
const copy = async (txt) => {
  await navigator.clipboard.writeText(txt);
  toast.success("已复制");
};

const twitterURL = (url, text) =>
  `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;

const linkedinURL = (url, text) =>
  `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&title=${encodeURIComponent(text)}`;

/* -------------------------------------------------- */
/* 1. EditPartnerDialog                               */
/* -------------------------------------------------- */
export function EditPartnerDialog({ partnerId, partner, canEdit }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(partner);
  const [preview, setPreview] = useState(false);
  const inputFile = useRef(null);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  // 上传 Logo
  const handleLogo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const path = `partners/${partnerId}/logo_${Date.now()}`;
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    setForm({ ...form, logo: url });
  };

  const save = async () => {
    const refDoc = doc(db, "partners", partnerId);
    // 写入主文档
    await updateDoc(refDoc, { ...form, updatedAt: serverTimestamp() });
    // 写入版本历史
    await setDoc(
      doc(db, "partners", partnerId, "revisions", Date.now().toString()),
      { ...form, ts: serverTimestamp() }
    );
    toast.success("已保存");
    setOpen(false);
  };

  if (!canEdit) return null;

  return (
    <>
      <button className="btn-edit" onClick={() => setOpen(true)}>
        <Pencil size={14} /> Edit
      </button>

      <Dialog open={open} onClose={() => setOpen(false)} className="dialog">
        <div className="dialog-backdrop" aria-hidden="true" />
        <div className="dialog-panel">
          <Dialog.Title className="dialog-title">
            编辑伙伴信息
            <button onClick={() => setOpen(false)}>
              <X size={18} />
            </button>
          </Dialog.Title>

          <div className="dialog-body">
            {/* 预览切换 */}
            <label className="switch">
              <input
                type="checkbox"
                checked={preview}
                onChange={() => setPreview(!preview)}
              />
              <span>预览模式</span>
            </label>

            {!preview ? (
              <form className="edit-form">
                <label>
                  名称
                  <input
                    name="name"
                    value={form.name || ""}
                    onChange={handleChange}
                    required
                  />
                </label>

                <label>
                  简介
                  <input
                    name="description"
                    value={form.description || ""}
                    onChange={handleChange}
                  />
                </label>

                <label>
                  Logo URL
                  <input
                    name="logo"
                    value={form.logo || ""}
                    onChange={handleChange}
                  />
                  <input
                    ref={inputFile}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleLogo}
                  />
                  <button
                    type="button"
                    onClick={() => inputFile.current.click()}
                  >
                    上传
                  </button>
                </label>

                {/* 其它字段可继续补充 —— tags、SEO 等 */}
              </form>
            ) : (
              <div className="preview-box">
                <h3>{form.name}</h3>
                <p>{form.description}</p>
                {form.logo && <img src={form.logo} alt="logo" />}
              </div>
            )}
          </div>

          <div className="dialog-footer">
            <button onClick={save}>
              <Save size={16} /> 保存
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
}

/* -------------------------------------------------- */
/* 2. ShareMenu                                       */
/* -------------------------------------------------- */
export function ShareMenu({ url, title, logo, partnerId }) {
  const [open, setOpen] = useState(false);
  const embed = `<a href="${url}?utm_source=lextrack"><img src="${logo}" alt="${title}" /></a>`;

  // 如果浏览器支持原生分享，可直接走
  const nativeShare = () =>
    navigator.share?.({ title, url }).catch(() => {});

  return (
    <div className="relative inline-block">
      <button className="btn-share" onClick={() => setOpen(!open)}>
        <Share2 size={14} /> Share
      </button>

      {open && (
        <div className="share-menu">
          <button onClick={() => copy(url)}>
            <Clipboard size={14} /> Copy Link
          </button>

          {navigator.share && (
            <button onClick={nativeShare}>
              <Share2 size={14} /> Share
            </button>
          )}

          <a href={twitterURL(url, title)} target="_blank" rel="noreferrer">
            <Twitter size={14} /> Twitter
          </a>
          <a href={linkedinURL(url, title)} target="_blank" rel="noreferrer">
            <Linkedin size={14} /> LinkedIn
          </a>

          <button onClick={() => copy(embed)}>
            <Code size={14} /> Embed
          </button>

          <div className="qr-wrapper">
            <QrCode size={14} /> QR Code
            <QRCodeCanvas value={`${url}?utm_source=lextrack`} size={88} />

          </div>
        </div>
      )}
    </div>
  );
}
